import { useLazyQuery } from '@apollo/client/react';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ScrollBox } from '~/components/blocks';
import { BASE_DATE } from '~/constants/organization_notification';
import { GetAnsweredQuestionnaireSheetsDocument } from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import {
  medicationFollowupNotificationPanelState,
  notificationState,
} from '~/state/layouts/SharedAppShell/atoms';

export const useFetchAnsweredQuestionnaireSheets = () => {
  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);
  const { followupCount } = useAtomValue(notificationState);
  const [{ page, perPage }, setMedicationFollowupNotificationPanelState] = useAtom(
    medicationFollowupNotificationPanelState,
  );
  const [getAnsweredSheet, { data, loading }] = useLazyQuery(
    GetAnsweredQuestionnaireSheetsDocument,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (loading) return;
    if (!data) return;

    const pagesCount =
      getMe(data)?.organization.medicationFollowupQuestionnaireSheets.pagesCount || 0;
    const nodesCount =
      getMe(data)?.organization.medicationFollowupQuestionnaireSheets.nodesCount || 0;
    setMedicationFollowupNotificationPanelState((_state) => ({
      ..._state,
      totalPage: pagesCount,
      totalCount: nodesCount,
    }));
    scrollRef.current?.toTop();
  }, [data, loading, setMedicationFollowupNotificationPanelState]);

  const [isOnlyUnread, setOnlyUnread] = useState<boolean>(true);
  const handleGetOnlyUnread = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMedicationFollowupNotificationPanelState((_state) => ({ ..._state, page: 1 }));
      setOnlyUnread(e.currentTarget.checked);
    },
    [setMedicationFollowupNotificationPanelState],
  );

  useEffect(() => {
    getAnsweredSheet({
      variables: {
        page: page,
        perPage: perPage,
        from: BASE_DATE,
        onlyUnread: isOnlyUnread,
      },
    });
  }, [getAnsweredSheet, page, perPage, followupCount, isOnlyUnread]);

  return { data, loading, scrollRef, isOnlyUnread, handleGetOnlyUnread };
};
